import { ChildRef } from '../../../../../lib/web/components/child-ref';
import { ClickListener } from '../../../../../lib/web/components/click-listener';
import { Component } from '../../../../../lib/web/components/component';
import { ComponentBase } from '../../../../../lib/web/components/component-base';
import { EventListener } from '../../../../../lib/web/components/event-listener';
import { MouseEnterListener } from '../../../../../lib/web/components/mouse-enter-listener';
import { Dom } from '../../../../../lib/web/core/dom';
import { Str } from '../../../../../lib/web/core/str';
import { TimeLineComponent } from '../../atoms/time-line/time-line.component';
import { VolumneSelectorComponent } from '../../atoms/volume-selector/volume-selector.component';
import { QualityMenuComponent } from '../../atoms/quality-menu/quality-menu.component';
import { AudioMenuComponent } from '../../atoms/audio-menu/audio-menu.component';
import { SessionService } from '../../../services/session.service';
import { CountdownButtonComponent } from '../../atoms/countdown-button/countdown-button.component';
import { RowComponent } from '../../organisms/row/row.component';
import { IconComponent } from '../../atoms/icon/icon.component';
import { DataEventService } from '../../../services/data-event.service';
import { MiniPlayerMenuComponent } from '../../atoms/mini-player-menu/mini-player-menu.component';
import { Agent } from '../../../../../lib/web/core/agent';
import { CookieService } from '../../../../../lib/web/services/cookie.service';
import { PlayerSegmentsComponent } from '../player-segments/player-segments.component';
import { ApiService } from '../../../services/api.service';
import { PlaybackRateMenuComponent } from '../../atoms/playbackrate-menu/playbackrate-menu.component';
import { SignLanguagesMenuComponent } from '../../atoms/sign-languages-menu/sign-languages-menu.component';
import { LogGroup } from '../../../../../lib/web/services/client-log.service';
import { EjsService } from '../../../services/ejs.service';
import { MediaService } from '../../../services/media.service';

export interface VjsPlayerMetadata {
    media?: { 
        id: string,
        type: 'video' | 'audio',
        manifests: { type: string, url: string, drm: any }[]
    };
    capsuleId?: number;
    collectionId?: number;
    watchingProgress?: {
        isLastCollectionChapter: boolean;
        nextCapsuleId: number;
        nextCapsuleDuration: number;
        signLanguage: number;
    };
    image?: string;
    display?: string;
    currentTime?: number;
    isContinueWatching?: boolean;
    title?: string;
    previous?: {
        url?: string;
        title?: string;
   },   
   next?: {
        url?: string;
        title?: string;
        isComingSoon?: boolean;
   },
   related?: any;
   rating?: any;
   ratingTags?: any[];
   disciplines?: { display: string }[];
   typology?: { display: string };
   progressPercentages?: number[];
   segments?: any[];
   subtitle?: string;
   audio?: string;
   isUserRequired?: boolean;
   timeMarkers?: {
        type: 'intro'|'credits';
        start: number;
        end: number;
   }[];
   signLanguages?: {
        locale: number;
        media?: { 
            id: string,
            type: 'video' | 'audio',
            manifests: { type: string, url: string }[]
        }; 
   }[];
}

export interface VjsPlayerOptions {
    controls?: boolean;
    playbackRates?: number[];
    miniPlayer?: 'enable' | 'disable';
    enableFullScreen?: boolean;
    chromecast?: boolean;
    back?: boolean;
    minimize?: boolean;
    skip?: boolean;
    skipText?: string;
    embed?: boolean;
    isPreview?: boolean;
    closeOnEnd?: boolean;
}

const TRANSITION_TIME: number = 1000;

@Component({
    selector: '.m-vjs-player'
})
export class VjsPlayerComponent extends ComponentBase<HTMLInputElement> {

    private _youboraPlugin: any = null;
    private _player: any = null;
    private _hideOverlaysTimeout: NodeJS.Timeout = null;
    private _isFirstPlay: boolean = false;
    private _isLoaded: boolean = false;
    private _metadata: VjsPlayerMetadata = null;
    private _options: VjsPlayerOptions = null;
    private _isWaiting: boolean = false;
    private _showRatingPending: boolean = null;
    private _qualitiesLoaded: boolean = false;
    private _playbackRatesLoaded: boolean = false;
    private _audioLoaded: boolean = false;
    private _signLanguagesLoaded: boolean = false;
    private _castContext: any = null;
    private _isChromecastWaitingObserver: MutationObserver = null;
    private _progressTracking: Map<number, number> = new Map<number, number>();
    private _lastProgressTracking: number = 0;
    private _relatedClosed: boolean = false;

    @ChildRef('.m-vjs-player__container')
    private _containerElement: HTMLDivElement = null;
    @ChildRef('.m-vjs-player__overlays')
    private _overlaysElement: HTMLDivElement = null;
    @ChildRef('.m-vjs-player__skip-intro-overlay')
    private _skipIntroOverlayElement: HTMLDivElement = null;
    @ChildRef('.m-vjs-player__skip-overlay')
    private _skipOverlayElement: HTMLDivElement = null;
    @ChildRef('.m-vjs-player__full-screen')
    private _fullScreenElement: HTMLButtonElement = null;
    @ChildRef('.m-vjs-player__fullscreen-announcement')
    private _fullScreenAnnouncementElement: HTMLDivElement = null;
    @ChildRef('.m-vjs-player__playbackrate')
    private _playbackRateButton: HTMLButtonElement = null;
    @ChildRef('.m-vjs-player__rating')
    private _ratingElement: HTMLDivElement = null;
    @ChildRef('.m-vjs-player__rating-alert')
    private _ratingAlertElement: HTMLAudioElement = null;
    @ChildRef('.m-vjs-player__chromecast .a-icon')
    private _chromecastIcon: IconComponent = null;
    @ChildRef()
    private _volumeSelector: VolumneSelectorComponent = null;
    @ChildRef()
    private _qualityMenu: QualityMenuComponent = null;
    @ChildRef()
    private _playbackRateMenu: PlaybackRateMenuComponent = null;
    @ChildRef()
    private _audioMenu: AudioMenuComponent = null;
    @ChildRef()
    private _signLanguagesMenu: SignLanguagesMenuComponent = null;
    @ChildRef()
    private _miniPlayerMenu: MiniPlayerMenuComponent = null;
    @ChildRef()
    private _timeLine: TimeLineComponent = null;
    @ChildRef()
    private _nextCountdown: CountdownButtonComponent = null;
    @ChildRef()
    private _relatedRow: RowComponent = null;    
    @ChildRef()
    private _playerSegments: PlayerSegmentsComponent = null;  

    public constructor(node: HTMLInputElement,
        private _sessionService: SessionService, 
        private _cookieService: CookieService,
        private _apiService: ApiService, 
        private _dataEventService: DataEventService,
        private _ejsService: EjsService,
        private _mediaService: MediaService) {
            
        super(node);

        this.addWindowEventListener('fullscreenchange', e => {
            this.setFullScreenMode();
        });

        this.addWindowEventListener('resize', () => {
            if (this.isMiniPlayerActive) {
                Dom.removeDraggable(this.node);
            }
        });

        this.addWindowEventListener('keydown', e => {
            this.onFocusChange(e);
        });
    }   

    public onInit(): void {
        super.onInit();        
        
        this.addKeyboardEventListeners();
        
        this._volumeSelector.addCustomEventListener('input', () => {
            this.setVolume(this._volumeSelector.value);
        });
        this._timeLine.addCustomEventListener('input', () => {
            this.setCurrentTime({ value: this._timeLine.value, action: 'absolute' });
            this.dispatchCustomEvent('user-update-current-time');
        });
        this._qualityMenu.addCustomEventListener('selection-changed', () => {
            this.changeQuality(this._qualityMenu.selectedQuality);
        });
        this._playbackRateMenu?.addCustomEventListener('rate-changed', () => {
            this.changePlaybackRate(this._playbackRateMenu.selectedPlaybackRate);
        });
        this._audioMenu.addCustomEventListener('text-track-changed', () => {
            this.onTextTrackChanged();
        });
        this._audioMenu.addCustomEventListener('audio-track-changed', () => {
            this.onAudioTrackChanged();
        });
        this._signLanguagesMenu.addCustomEventListener('sign-language-changed', e => {            
            this.dispatchCustomEvent('sign-language-changed', e);
        });
        this._playerSegments.addCustomEventListener('selected', segment => {
            this.setCurrentTime({ value: segment.position, action: 'absolute' });
            this._dataEventService.selectFragment(this.mediaId, this._metadata.display, 'player', segment.title);
            this.closeSegments();
        });
        this._miniPlayerMenu.addCustomEventListener('mini-player', () => this.toggleMiniPlayer());
        this._miniPlayerMenu.addCustomEventListener('pip', () => this.toggleMiniPlayer({ pip: true }));
        this._nextCountdown.addCustomEventListener('end', () => this.onNextCountdown());
        if (!document.pictureInPictureEnabled) {
            this._node.dataset.pipDisabled = 'true';
        }
        document.body.focus();
    }
   
    @ClickListener('.m-vjs-player__play')
    public onPlay(): void {
        this.play(this._player);
        this.setHideOverlaysTimeout();
    }

    @ClickListener('.m-vjs-player__pause')
    public onPause(): void {
        this._player?.pause();
        this.setHideOverlaysTimeout();
    }

    @ClickListener('.m-vjs-player__rewind') 
    public onRewind(): void {
        this.setCurrentTime({ value: -10, action: 'relative' });        
        this.setHideOverlaysTimeout();
        this.dispatchCustomEvent('user-update-current-time');
    }

    @ClickListener('.m-vjs-player__forward') 
    public onForward(): void {
        this.setCurrentTime({ value: 10, action: 'relative' });
        this.setHideOverlaysTimeout();
        this.dispatchCustomEvent('user-update-current-time');
    }

    @ClickListener('.m-vjs-player__back') 
    public onBack(): void {
        this.dispatchCustomEvent('back');
    }

    @ClickListener('.m-vjs-player__close-mini-player') 
    public onCloseMiniPlayer(): void {
        this.closeMiniPlayer();
    }

    @ClickListener('.m-vjs-player__expand-mini-player') 
    public onExpandMiniPlayer(): void {
        this.toggleMiniPlayer();
    }

    @ClickListener('.m-vjs-player__full-screen') 
    public onToggleFullScreen(): void {
        Dom.toggleFullScreen(Agent.iOS() ? this._node.querySelector('video'): null);
        this.setHideOverlaysTimeout();
    }

    @ClickListener('.m-vjs-player__volume') 
    public onToggleVolume(e: MouseEvent): void {
        e.stopPropagation();
        this.setVolume(this._player?.muted() ? 'on': 'off');                            
        this.setHideOverlaysTimeout();
    }

    @ClickListener('.m-vjs-player__quality') 
    public onToggleQuality(e: MouseEvent): void {
        e.stopPropagation();
        this.openMenu('quality');
    }

    @ClickListener('.m-vjs-player__playbackrate') 
    public onTogglePlaybackRate(e: MouseEvent): void {
        e.stopPropagation();
        this.openMenu('playbackRate');
    }

    @ClickListener('.m-vjs-player__audio') 
    public onToggleAudio(e: MouseEvent): void {
        e.stopPropagation();
        this.openMenu('audio');
    }

    @ClickListener('.m-vjs-player__sign-languages') 
    public onToggleSignLanguages(e: MouseEvent): void {
        e.stopPropagation();
        this.openMenu('sign-languages');
    }

    @ClickListener('.m-vjs-player__mini-player') 
    public onToggleMiniPlayer(e: MouseEvent): void {
        e.stopPropagation();
        this.toggleMiniPlayer({ pip: true });
    }

    @ClickListener('.m-vjs-player__pip') 
    public onPip(e: MouseEvent): void {
        e.stopPropagation();
        this.requestPictureInPicture();        
    }

    @ClickListener('.m-vjs-player__chromecast') 
    public onChromecast(e: MouseEvent): void {
        e.stopPropagation();
        const chromecastButton: HTMLButtonElement = document.querySelector('.vjs-chromecast-button');
        if (chromecastButton) {
            chromecastButton.click();
        }
    }

    @ClickListener('.m-vjs-player__previous-chapter') 
    public onPreviousChapter(): void {
        this.hideSkipIntro();
        this.dispatchCustomEvent('previous-chapter', this._metadata.previous);
    }

    @ClickListener('.m-vjs-player__next-chapter') 
    public onNextChapter(): void {
        this.hideSkipIntro();
        this.dispatchCustomEvent('next-chapter', this._metadata.next);
    }

    @ClickListener('.m-vjs-player__next-overlay .a-countdown-button')
    public onNextCountdown(): void {
        this._nextCountdown.stop();
        this.onNextChapter();
    }

    @ClickListener('.m-vjs-player__segments')
    public onShowSegments(): void {
        this.showSegments();
    }

    @ClickListener('.m-vjs-player__close-segments')
    public onCloseSegments(): void {
        this.closeSegments();
    }

    @ClickListener('.m-vjs-player__related-close')
    public onCloseRelated(): void {
        this.closeRelated();
    }

    @ClickListener('.m-vjs-player__next-close')
    public onCloseNext(): void {
        this.closeRelated();
    }

    @EventListener('mousemove')
    public onMouseMove(): void {
        this.showOverlays();
        this.setHideOverlaysTimeout();
    }

    public onFocusChange(e: KeyboardEvent): void {
        if (this._player) {
            switch (e.key) {
                case 'Tab':
                    this.showOverlays();
                    this.setHideOverlaysTimeout();
                    break;
                case 'Enter':
                    this.showOverlays();
                    this.setHideOverlaysTimeout();
                    break;
                case ' ':
                case 'Spacebar':
                    // Spacebar toggles play/pause
                    this.showOverlays();
                    this.setHideOverlaysTimeout();
                    if (this._node.dataset.status === 'playing') {
                        this._player.pause();
                    } else {
                        this.play(this._player);
                    }
                    break;
            }
        }
    }

    @MouseEnterListener('.m-vjs-player__volume')
    public onMouseEnter(): void {
        if (!this.isTouch) {
            this.openMenu('volume');
        }
    }

    @ClickListener('.m-vjs-player__overlays')
    public onClickOverlays(e: MouseEvent): void {
        if ((e.target as HTMLElement).closest('.a-audio-menu') == null 
            && (e.target as HTMLElement).closest('.a-sign-languages-menu') == null 
            && (e.target as HTMLElement).closest('.a-playbackrate-menu') == null 
            && (e.target as HTMLElement).closest('a-quality-menu') == null 
            && (e.target as HTMLElement).closest('a-volume-selector') == null ) {
            this.openMenu(null);
        }
    }    

    @ClickListener('.m-vjs-player__skip-intro') 
    public onSkipIntro(e: MouseEvent): void {
        e.stopPropagation();
        this.skipIntro();
    }

    @ClickListener('.m-vjs-player__skip') 
    public onSkipVideo(e: MouseEvent): void {
        e.stopPropagation();
        this.closePlayer({ skipped: true });
    }

    @ClickListener()
    public onClick(): void {
        if (this.isTouch) {
            this.showOverlays();
            this.setHideOverlaysTimeout();
        }
    }

    private restart(): void {
        const metadata: VjsPlayerMetadata = { ...this._metadata };
        const options: VjsPlayerOptions = { ...this._options };
        metadata.audio = this._audioMenu.selectedAudioTrack;
        metadata.subtitle = this._audioMenu.selectedTextTrack;
        metadata.currentTime = this.currentTime || 0;
        metadata.isContinueWatching = true;
        this.unload();
        this.load(metadata, options);
    }

    private reset(metadata: VjsPlayerMetadata, options?: VjsPlayerOptions): void {
        window.videojs = window.vjsPlayer;
        this.hideSkipIntro();
        this._relatedClosed = false;
        this._progressTracking.clear();
        this._lastProgressTracking = 0;
        this._metadata = metadata;
        this._options = options;
        const { previous, next, title, segments } = this._metadata;
        delete this._node.dataset.status;
        delete this._node.dataset.firstPlay;
        this.hideOverlays();
        this._isFirstPlay = false;
        this.setInnerHTML(title, '.m-vjs-player__title')
        this.addOrRemoveClass(!previous?.url && !next?.url, 'u-hidden-important', '.m-vjs-player__chapters-navigation');
        this.addClass('u-hidden', '.m-vjs-player__mini-player-control');
        if (previous?.url) {
            this.enable('.m-vjs-player__previous-chapter');
        }
        else {
            this.disable('.m-vjs-player__previous-chapter');
        }
        if (next?.url && !next.isComingSoon) {
            this.enable('.m-vjs-player__next-chapter');
            this.setNext();
        }
        else {
            this.disable('.m-vjs-player__next-chapter');
        }
        this.addOrRemoveClass(!(segments?.length > 0), 'u-hidden', '.m-vjs-player__segments');
        this._playerSegments.set(segments);
        this._ratingElement.innerHTML = this.rating?.code;
        this._ratingElement.style.backgroundColor = this.rating?.color;   
        this._showRatingPending = null; 
        this.setInnerHTML((this.ratingTags || []).map((t, i) => i == 0 ? t.name: t.name.toLowerCase()).join(', '), '.m-vjs-player__rating-tags');
    }   

    public load(metadata: VjsPlayerMetadata, options?: VjsPlayerOptions): void {
        this.loadYouboraScript();
        
        if (options.enableFullScreen !== false) {
            Dom.onFullScreen();
            setTimeout(() => {
                if (!Dom.isFullScreen() && Agent.iOS()) {
                    Dom.onFullScreen(this._node.querySelector('video'));
                }
            }, 10);
        }
        this.reset(metadata, options);

        this.disposeVjsPlayers();
        const videoJsElement: HTMLElement = document.createElement('video-js');
        videoJsElement.id = 'vjs-player';

        this._containerElement.appendChild(videoJsElement);   
        
        this.loadPlayerScript(() => this.onPlayerLoaded());            
    }

    private async loadMedia(): Promise<void> {
        const source: { src: string, type: string, keySystems?: any } = await this._mediaService.getSource(this._metadata.media?.manifests);
        if (source) {
            if (source.keySystems?.['com.apple.fps.1_0']) {
                // videojs-contrib-eme picks its DRM path with `if (window.MediaKeys) ... else if
                // (window.WebKitMediaKeys)`. Current Safari exposes both, so it always takes the
                // modern EME branch, while licenseUri/certificateUri/getContentId are only read by
                // the legacy FairPlay handler and the key exchange never starts. Wire it explicitly
                // on loadstart, once the tech element exists.
                this._player.one('loadstart', () => this.initLegacyFairplay());
            }
            this._player.src(source);
        }
    }

    private initLegacyFairplay(): void {
        const eme: any = (this._player as any).eme;
        if (!eme?.initLegacyFairplay || eme.legacyFairplayIsUsed) {
            return;
        }
        try {
            eme.initLegacyFairplay();
        }
        catch (e: any) {
            logger.error('Could not initialise legacy FairPlay:', e);
        }
    }
   
    private async onPlayerLoaded(): Promise<void> {
        const { controls } = this._options || {};

        const vjsOptions: any = {
            controls: controls === true,
            poster: this._metadata?.image, 
            techOrder: CONFIG.CHROMECAST_APP_ID ? [ 'chromecast', 'html5' ] : ['html5'],
            chromecast: CONFIG.CHROMECAST_APP_ID ? this.getChromecastConfig() : undefined,
            plugins: {
                chromecast: CONFIG.CHROMECAST_APP_ID ? {
                    receiverAppID: CONFIG.CHROMECAST_APP_ID,
                    addButtonToControlBar: true,
                }: undefined
            }          
        };
        this._player = window.videojs('vjs-player', vjsOptions);
        this._player.eme();

        if (this._metadata.media?.type === 'audio') {
            this._player.addClass('vjs-audio');
        }
        this.setAudioMenuData();
        this.setOptions();

        this.addCustomEventListener('enterpictureinpicture', () => {
            this._node.dataset.pip = 'true';
            if (!this.isMiniPlayerActive) {
                this.toggleMiniPlayer();
            }
        }, 'video');
        this.addCustomEventListener('leavepictureinpicture', () => {
            delete this._node.dataset.pip;
            if (this.isMiniPlayerActive) {
                this.toggleMiniPlayer();      
            }      
        }, 'video');
        
        this._isLoaded = true;
        if (CONFIG.CHROMECAST_APP_ID) {
            this._player.on('chromecastConnected', (e: any) => {
                this.isChromecastActive = true;
                this.stopYoubora();
            });
            this._player.on('chromecastDisconnected', (e: any) => {
                if (this.isChromecastActive) {
                    this.isChromecastActive = false;
                    this.restart();
                }
            });
        }
        this._player.on('error', () => {
            this.processError();
        });
        this._player.on('touchstart', () => {
            this.onClick();
        });
        this._player.on('timeupdate', (e: any) => {
            this.setInnerHTML(Str.getProgress(this._player.currentTime(), this._player.duration()), '.m-vjs-player__progress');
            const currentTime: number = this._player.currentTime();
            this._timeLine.value = currentTime;
            this.onProgressTracking(currentTime);
            this.onCheckTimeMarker(currentTime);
        }); 
        this._player.on('loadedmetadata', async (e: any) => {
            this._timeLine.max = this._player.duration();                                    
            this.tryPlay();
        });               
        this._player.on('play', (e: any) => {
            if (!this._isFirstPlay) {
                this.setVolume(this._player.muted() ? 'off': this._player.volume());
                this._node.dataset.firstPlay = 'true';            
                this._node.dataset.status = 'playing';
                this._isFirstPlay = true;
                const { currentTime } = this._metadata;
                if (currentTime) {
                    this.setCurrentTime({ value: currentTime, action: 'absolute'});
                }
            }
            if (this.node.dataset.status !== 'playing') {
                this._dataEventService.videoAction(this.mediaId, 'play');
            }
            this._node.dataset.status = 'playing';
            this.showOverlays();
            this.setHideOverlaysTimeout();
            if (this._showRatingPending == null) {
                this._showRatingPending = true;
            }
        });
        this._player.on('pause', (e: any) => {
            if (this.node.dataset.status !== 'paused') {
                this._dataEventService.videoAction(this.mediaId, 'pause');
            }
            this._node.dataset.status = 'paused';
            this.setHideOverlaysTimeout();
            this.dispatchCustomEvent('pause');
        });

        // Showing loader events
        this._player.on('waiting', (e: any) => {
            this.onIsWaitingChanged(true);
        });
        this._player.on('seeking', (e: any) => {
            this.onIsWaitingChanged(true);
        });
        // Hiding loader events
        this._player.on('seeked', (e: any) => {
            this.onIsWaitingChanged(false);
        });
        this._player.on('canplay', (e: any) => {
            this.onIsWaitingChanged(false);
            this.addQualities();
            this.addPlaybackRates();
            this.addAudios();
            this.addSignLanguages();
            if (CONFIG.CHROMECAST_APP_ID) {
                this.setupChromecast();
            }
            this.setOptions();
        });
        this._player.on('canplaythrough', (e: any) => {
            this.onIsWaitingChanged(false);
        });
        this._player.on('playing', (e: any) => {
            this.onIsWaitingChanged(false);
        });
        this._player.on('ended', async (e: any) => {
            this._dataEventService.videoAction(this.mediaId, 'video_completed');
            if (this.isMiniPlayerActive) {
                this.closeMiniPlayer();
            }
            else if (this._options?.closeOnEnd) {
                this.closePlayer({ ended: true });
            }
            else {
                this._node.dataset.status = 'ended';
                this._timeLine.value = this._timeLine.max;
                this.onIsWaitingChanged(false);
                this.showOverlays();
                this.dispatchCustomEvent('ended');
            }
        });
        await this.loadMedia();
    }

    private getChromecastConfig(): any {
        return {
            requestTitleFn: () => this._metadata.title,
            requestSubtitleFn: () => USER_LOCALE.siteName,
            requestCustomDataFn: (customData: any) => {
                const chromecastManifest: any = this._mediaService.getManifest(this._metadata.media.manifests, true);
                customData.manifest = {
                    url: chromecastManifest.url,
                    licenseUrl: chromecastManifest.drm?.licenseUrl
                };
                customData.metadata = {
                    capsuleId: this._metadata.capsuleId,
                    collectionId: this._metadata.collectionId,
                    watchingProgress: this._metadata.watchingProgress,
                    mediaType: this._metadata.media.type
                };
                const token: string = this._cookieService.get('auth-token');
                if (token) {
                    customData.auth = {
                        token
                    };
                }
                if (!this._options.isPreview) {
                    const options = this.getYouboraOptions('cast');
                    customData.youbora = options;
                }
                return customData;
            },
            modifyLoadRequestFn: (loadRequest: any) => {
                logger.notice(`📺 Chromecast load request: ${JSON.stringify(loadRequest)}`);
                setTimeout(() => {
                    this.onAudioTrackChanged(true);
                    this.onTextTrackChanged(true);
                }, 3000);
                return loadRequest;                
            }
        };
    }

    private setAudioMenuData(): void {
        this._audioMenu.setData({
            player: {
                play: () => this.play(this._player),
                pause: () => this._player.pause(),
                isPlaying: () => this._node.dataset.status == 'playing'
            },
            fontSize: {
                getValue: () => {
                    return this._player.textTrackSettings.getValues().fontPercent || 1;
                },
                setValue: (value) => {
                    let fontSize: string = null;
                    switch (value) {
                        case 0.5:
                            fontSize = '0.50';
                            break;
                        case 0.75:
                            fontSize = '0.75';
                            break;
                        case 1:
                            fontSize = '1.00';
                            break;
                        case 1.5:
                            fontSize = '1.50';
                            break;
                        case 2:
                            fontSize = '2.00';
                            break;
                        case 3:
                            fontSize = '3.00';
                            break;
                        case 4:
                            fontSize = '4.00';
                            break;                    
                    }
                    document.querySelector('.vjs-font-percent').querySelector('select').value = fontSize || '1.00';
                },    
            }
        });
    }

    public youboraUpdateOption(key: string, value: string) {
        if (typeof youbora !== 'undefined' && this._youboraPlugin && !this._options.isPreview) {
            logger.log('🎥 youbora update option', key, value);            
            this._youboraPlugin.setOptions({ [key]: value });
        }
    }

    private onYouboraLoaded(): void {    
        if (window.isYouboraPluginScriptLoaded && !this._options.isPreview) {
            if (typeof youbora !== 'undefined') {
                logger.notice('🎥 initializing youbora plugin');
                this._youboraPlugin = new youbora.Plugin({ accountCode: CONFIG.YOUBORA_ACCOUNT_CODE });            

                const options: any = this.getYouboraOptions('web');
                logger.log('🎥 youbora options', options);

                this._youboraPlugin.setOptions(options);
                this._youboraPlugin.setAdapter(new youbora.adapters.Videojs(this._player));
            }
        }
    }

    private getYouboraOptions(target: 'web' | 'cast'): any {
        const { user } = this._sessionService.get() || {};

        let options: any = {
            accountCode: CONFIG.YOUBORA_ACCOUNT_CODE,
            username: user?.id,
            'content.transactionCode': CONFIG.YOUBORA_TRANSACTION_CODE,
            'content.title': this.mediaId,
            'content.isLive': false,
            'content.channel': target === 'web' && location.ancestorOrigins?.length ? location.ancestorOrigins[0] : undefined,
            'content.customDimension.1': 'web',                
            'content.customDimension.2': target === 'web' ? `${this._player?.playbackRate()}x` || '1x' : '1x',
            'content.customDimension.3': this._audioMenu?.selectedAudioTrackLanguage || 'default',
            'content.customDimension.4': this._audioMenu?.selectedTextTrack || 'disabled',
            "content.metadata": {
                sessionId: this._sessionService.getSessionId()
            }
        };
        const { isContinueWatching } = this._metadata || {};
        if (isContinueWatching) {
            options = {
                ...options,
                'content.playbackType': 'catch-up'
            };
        }
        return options;
    }

    private stopYoubora(): void {
        if (this._youboraPlugin) {
            const originalOnError = window.onerror;
            window.onerror = (message) => {
                if (typeof message === 'string' && message.includes(`Cannot read properties of null (reading 'currentTime')`)) {
                    logger.log("🎥 caught and suppressed late Youbora poller error.");
                    return true;
                }
                return false;
            };
            this._youboraPlugin.getAdapter().fireStop();
            this._youboraPlugin.removeAdapter();
            this._youboraPlugin = null;
            logger.log('🎥 youbora adapter removed');
            this._youboraPlugin = null;

            setTimeout(() => {
                window.onerror = originalOnError;
            }, 500);
        }
    }
   
    private async tryPlay(): Promise<void> {
        if (this._node.dataset.status != 'playing' && !(this._options || {}).embed) {
            if (!(await this.autoplay())) {
                this.showOverlays();
            }
        }
        if (this._node.dataset.status != 'playing' && (this._options || {}).embed) {
            this.showOverlays();
        }
    }

    private async autoplay(): Promise<boolean> {
        return new Promise<boolean>(async (resolve: (value: boolean) => void) => {            
            setTimeout(() => {
                if (this._node.dataset.status != 'playing') {
                    resolve(false);
                }
            }, 2000);
            try {
                this._player.play().then(() => {
                    resolve(true);
                }).catch(async () => {
                    try {
                        if (!this._player.muted()) {
                            this.setVolume('off');
                            if (await this.autoplay()) {
                                resolve(true);
                            }     
                        }   
                        else {
                            resolve(false);
                        }   
                    }
                    catch (e: any) {
                        resolve(false);
                    }
                });
            }
            catch (e: any) {
                resolve(false);
            }
        });
    }

    private setCurrentTime(time: { value?: number, action?: 'absolute' | 'relative' }): void {
        if (this._player) {
            const { value, action } = time;
            let targetTime: number = action == 'absolute' ? value: (this._player.currentTime() || 0) + value;
            if (targetTime < 0) {
                targetTime = 0;
            }
            else if (targetTime > this._player.duration()) {
                targetTime = this._player.duration();
            }
            this._player.currentTime(targetTime);
        }
    }

    private setVolume(volume: number | 'on' | 'off'): void {
        if (this._player) {
            volume = volume || 0;
            if (typeof volume == 'number') {
                this._player.volume(volume);
                if (this._player.volume() > 0) {
                    this._player.muted(false);
                    if (this._volumeSelector.value != this._player.volume()) {
                        this._volumeSelector.value = this._player.volume();
                    }     
                }
            }
            else if (volume == 'off') {
                this._player.muted(true);
            }
            else if (volume == 'on') {
                this._player.muted(false);  
                if (this._player.volume() == 0) {
                    this._player.volume(1);
                }
                if (this._volumeSelector.value != this._player.volume()) {
                    this._volumeSelector.value = this._player.volume();
                }
            }        
            if (this._player.volume() == 0 || this._player.muted()) {
                this.addClass('m-vjs-player__volume--muted', '.m-vjs-player__volume');
                this._player.muted(true);
                if (this._volumeSelector.value > 0) {
                    this._volumeSelector.value = 0;
                }
            }
            else {
                this.removeClass('m-vjs-player__volume--muted', '.m-vjs-player__volume');            
            }
        }
    }

    private addQualities(): void {
        if (!this._qualitiesLoaded) {
            this._qualitiesLoaded = true;
            const qualityLevels: { selectedIndex_: number ,levels_: any[] } = this._player.qualityLevels();
            if (qualityLevels && qualityLevels.levels_ && qualityLevels.levels_.length) {
                this._qualityMenu.selectedQuality = (qualityLevels.levels_[qualityLevels.selectedIndex_] || {}).id;
                this._qualityMenu.qualities = qualityLevels.levels_.filter((quality: {id: string, height: number}) => quality.height).map((quality: {id: string, height: number}, index: number) => {
                    return {
                        id: quality.id, 
                        display: quality.height.toString(),
                        isSelected: qualityLevels.selectedIndex_ === index
                    };
                });
            }
            if ((this._qualityMenu.qualities || {}).length > 1) {
                this.removeClass('u-hidden', '.m-vjs-player__quality-control');
            }
        }
    }

    private changeQuality(qualityId: string): void {
        const qualityLevels = this._player.qualityLevels().levels_;
        qualityLevels.forEach((quality: {id: string, enabled: boolean}) => {
            if (quality.id === qualityId) {
                quality.enabled = true;
            }
            else {
                quality.enabled = false;
            }
        });
    }

    private addPlaybackRates(): void {
        if (!this._playbackRatesLoaded) {
            this._player.playbackRates([0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]);
            this._playbackRatesLoaded = true;
            const playbackRates: number[] = this._player.playbackRates();
            if (playbackRates && playbackRates.length) {
                this._playbackRateMenu.playbackRates = playbackRates.map((id: number) => {
                    const isSelected = id == 1;
                    if (isSelected) {
                        this._playbackRateButton.innerText = id + 'x';
                    }
                    return { id: id.toString(), isSelected };
                });
            }
            if ((this._playbackRateMenu.playbackRates || {}).length > 1) {
                this.removeClass('u-hidden', '.m-vjs-player__playbackrate-control');
            }
        }
    }

    private changePlaybackRate(playbackRateId: string): void {
        this._playbackRateMenu.playbackRates.forEach(({id, isSelected}) => {
            if (id === playbackRateId) {
                isSelected = true;
                this._player.playbackRate(playbackRateId);
                const display = id + 'x';
                this._playbackRateButton.innerText = display;
                this.youboraUpdateOption('extraparam.2', display);
            }
            else {
                isSelected = false;
            }
        });
    }

    private addAudios(options?: { selectedTextTrack?: string, selectedAudioTrack?: string }): void {        
        if (!this._audioLoaded) {
            const { selectedAudioTrack, selectedTextTrack } = options || {};
            this._audioLoaded = true;
            const audioTracks: { tracks_: { enabled: boolean, label: string, language: string , id: string }[] } = this._player.audioTracks();
            const textTracks: { tracks_: { id: string, mode: 'disabled' | 'hidden' | 'showing', kind: string, label: string, language: string, default: boolean }[] } = this._player.textTracks();

            this._audioMenu.addAudioTracks(audioTracks.tracks_);
            this._audioMenu.addTextTracks(textTracks.tracks_);

            if (selectedTextTrack || this._metadata.subtitle) {
                this._audioMenu.selectTextTrackLanguage(selectedTextTrack || this._metadata.subtitle);
            }
            if (selectedAudioTrack || this._metadata.audio) {
                this._audioMenu.selectAudioTrack(selectedAudioTrack || this._metadata.audio);
            }
            
            this.loadYouboraScript(() => this.onYouboraLoaded());

            this.removeClass('u-hidden', '.m-vjs-player__audio-control');
        }
    }

    private onTextTrackChanged(isChromecastTarget?: boolean): void {
        if (!isChromecastTarget) {
            this.dispatchCustomEvent('text-track-changed');
            if (this._audioMenu.selectedTextTrack && this._audioMenu.selectedTextTrack != 'disabled') {                
                if (this.sendDataEvent) {
                    this._dataEventService.textTrackEnabled(this.mediaId, this._metadata.display, this._metadata?.disciplines?.map(d => d.display), this._metadata?.typology?.display, this._audioMenu.selectedTextTrack);
                }
            }
            const subtitleLanguage = this._audioMenu.selectedTextTrackLanguage || 'disabled';
            this.youboraUpdateOption('extraparam.4', subtitleLanguage);
        }
        this.sendChromecastMessageReceiver({ type: 'text-track-changed', data: { index: this._audioMenu.selectedTextTrackIndex } });
    }

    private onAudioTrackChanged(isChromecastTarget?: boolean): void {
        if (!isChromecastTarget) {
            this.dispatchCustomEvent('audio-track-changed');
            const audioLanguage = this._audioMenu.selectedAudioTrackLanguage || 'default';
            this.youboraUpdateOption('extraparam.3', audioLanguage);
        }
        this.sendChromecastMessageReceiver({ type: 'audio-track-changed', data: { index: this._audioMenu.selectedAudioTrackIndex } });
    }

    private addSignLanguages(): void {
        if (!this._signLanguagesLoaded) {
            this._signLanguagesLoaded = true;

            if (this._metadata.signLanguages?.length) {
                this._signLanguagesMenu.addSignLanguages(this._metadata.signLanguages.map(s => ({ locale: s.locale, mediaId: s.media.id })), this._metadata.signLanguages.some(signLanguage => signLanguage.media.id == this.mediaId) ? this.mediaId: null);
                this.removeClass('u-hidden', '.m-vjs-player__sign-languages-control');   
            }   
        }
    }

    private onProgressTracking(currentTime: number): void {
        this._progressTracking.set(Math.floor(currentTime), Math.floor(currentTime));
        const currentProgress: number = Math.floor((Array.from(this._progressTracking.keys()).length / this.duration) * 100);
        if (this._lastProgressTracking != currentProgress) {
            this._lastProgressTracking = currentProgress;
            if ((this._metadata.progressPercentages || []).includes(this._lastProgressTracking)) {
                this.dispatchCustomEvent('percentage-seen', { percentage: this._lastProgressTracking });
            }
        }        
    }

    private onCheckTimeMarker(currentTime: number): void {
        const { timeMarkers } = this._metadata || {};
        const { embed } = this._options || {};
        if (timeMarkers?.length) {
            const marker: { type: 'intro' | 'credits', start: number, end: number } = timeMarkers.find(m => m.start <= currentTime && m.end >= currentTime);
            if (marker) {
                if (marker.type === 'intro') {                    
                    this.showSkipIntro();
                }
                if (marker.type === 'credits') {                    
                    this.showRelated();
                }
            }
            else {
                this.hideSkipIntro();
            }            
        }
    }

    private skipIntro(): void {
        const marker: { type: 'intro' | 'credits', start: number, end: number } = this._metadata.timeMarkers.find((m) => m.type === 'intro');
        if (marker) { 
            this.setCurrentTime({ value: marker.end, action: 'absolute' });
        }
    }

    private setupChromecast(): void {
        if (window.cast?.framework && !this._castContext) {            
            logger.notice('📺 Setting up chromecast plugin');
            this.stopChromecast();            
            const context: any = window.cast?.framework?.CastContext?.getInstance();
            if (context) {
                this._castContext = context;
                this._castContext.addEventListener(window.cast.framework.CastContextEventType.SESSION_STATE_CHANGED, this.chromecastSessionStateChangeHandler);
                this._castContext.addEventListener(window.cast.framework.CastContextEventType.CAST_STATE_CHANGED, this.chromecastCastStateChangeHandler);
                this.setChromecastState();
            }
        }
        else if (this._castContext) {
            this.setChromecastState();
        }
    }    

    private setChromecastState(): void {
        if (this._castContext) {
            logger.notice(new LogGroup('📺 Setting chromecast state'));
            this.chromecastSessionStateChangeHandler({ sessionState: this._castContext.getSessionState() });
            this.chromecastCastStateChangeHandler({ castState: this._castContext.getCastState() });    
            logger.closeGroup();
        }
    }

    private stopChromecast(): void {
        if (window.cast && window.cast.framework && window.cast.framework.CastContext && window.cast.framework.CastContext.getInstance() && window.cast.framework.CastContext.getInstance().getCurrentSession()) {
            window.cast.framework.CastContext.getInstance().getCurrentSession().endSession(true);
            this._chromecastIcon.key = 'chromecast-not-connected'; 
            if (this._castContext) {
                this._castContext.removeEventListener(window.cast.framework.CastContextEventType.SESSION_STATE_CHANGED, this.chromecastSessionStateChangeHandler);
                this._castContext.removeEventListener(window.cast.framework.CastContextEventType.CAST_STATE_CHANGED, this.chromecastCastStateChangeHandler);
                this._castContext = null;
            }
            this.isChromecastActive = false;
            if (this._isChromecastWaitingObserver) {
                this._isChromecastWaitingObserver.disconnect();
                this._isChromecastWaitingObserver = null;
            }
        }
    }

    private async sendChromecastMessageReceiver(message: { type: string, data: any }) {
        if (this.isChromecastActive) {
            const castSession = cast.framework.CastContext.getInstance().getCurrentSession();
            if (castSession) {
                logger.notice('📺 Sending message to chromecat receiver', message);
                const result = await castSession.sendMessage('urn:x-cast:player', JSON.stringify(message));
                logger.notice('📺 Message sent to chromecat receiver', result);
            }
        }
    }

    private chromecastSessionStateChangeHandler = (event: any) => {                                         
        const { NO_SESSION, SESSION_ENDED, SESSION_ENDING, SESSION_RESUMED, SESSION_STARTED, SESSION_STARTING, SESSION_START_FAILED } = window.cast.framework.SessionState;
        logger.notice(`📺 session: ${event.sessionState}`);
        switch (event.sessionState) {
            case SESSION_ENDING:
            case SESSION_ENDED:
            case SESSION_START_FAILED:
            case NO_SESSION:                        
                this.onIsChromcastWaiting(false);
                break;
            case SESSION_STARTED:
                this.onIsChromcastWaiting(true);                
                this._dataEventService.shareStreaming(this.mediaId, this._metadata.display, this._metadata?.disciplines?.map(d => d.display), this._metadata?.typology?.display, 'detail', 'Chromecast');
                break;
        }
    }

    private chromecastCastStateChangeHandler  = (event: any) => {
        const { CONNECTED, CONNECTING, NOT_CONNECTED, NO_DEVICES_AVAILABLE } = window.cast.framework.CastState;
        logger.notice(`📺 cast: ${event.castState}`);
        switch (event.castState) {
            case CONNECTED:
                this.removeClass('u-hidden', '.m-vjs-player__chromecast-control');
                this._chromecastIcon.key = 'chromecast-connected';                
                this.isChromecastActive = true;
                break;
            case CONNECTING:
                this.removeClass('u-hidden', '.m-vjs-player__chromecast-control');
                this._chromecastIcon.key = 'chromecast-connected';
                break;
            case NOT_CONNECTED:
                this.removeClass('u-hidden', '.m-vjs-player__chromecast-control');
                this._chromecastIcon.key = 'chromecast-not-connected';
                break;
            case NO_DEVICES_AVAILABLE:
                this.addClass('u-hidden', '.m-vjs-player__chromecast-control');
                this._chromecastIcon.key = 'chromecast-not-connected';
                break;
        }
    }    
  
    private setFullScreenMode(): void {
        const isFullScreen: boolean = Dom.isFullScreen();
        this.addOrRemoveClass(isFullScreen, 'm-vjs-player__full-screen--on', '.m-vjs-player__full-screen');
        
        const newLabel = isFullScreen ? USER_LOCALE.exitFullScreen : USER_LOCALE.fullScreen;
        this._fullScreenElement.dataset.tooltip = newLabel;
        this._fullScreenElement.setAttribute('aria-label', newLabel);
        
        if (this._fullScreenAnnouncementElement) {
            this._fullScreenAnnouncementElement.textContent = isFullScreen ? 
                USER_LOCALE.fullScreenActivated : USER_LOCALE.fullScreenDeactivated;
        }
        
        if (isFullScreen) {
            Dom.lockOrientation('landscape');
        }
        else {
            Dom.unlockOrientation();
        }
    }

    private addKeyboardEventListeners(): void {
        // Comprehensive keyboard navigation for all dropdown menus
        this._node.addEventListener('keydown', (e: KeyboardEvent) => {
            // Restart inactivity timeout on any key press
            this.showOverlays();
            this.setHideOverlaysTimeout();
            
            const activeElement = document.activeElement as HTMLElement;
            const dropdown = activeElement?.closest('.a-audio-menu, .a-quality-menu, .a-playbackrate-menu, .a-sign-languages-menu, .a-mini-player-menu');
            
            if (dropdown && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
                e.preventDefault();
                const buttons = dropdown.querySelectorAll('button, [role="button"][tabindex="0"], [role="menuitem"][tabindex="0"]');
                const current = Array.from(buttons).indexOf(activeElement);
                const next = e.key === 'ArrowDown' ? 
                    (current + 1) % buttons.length : 
                    current === 0 ? buttons.length - 1 : current - 1;
                (buttons[next] as HTMLElement).focus();
            }
            
            // Handle Escape key to close menus
            if (e.key === 'Escape' && dropdown) {
                e.preventDefault();
                const parentButton = this._node.querySelector(`[aria-expanded="true"]`) as HTMLElement;
                if (parentButton) {
                    parentButton.click(); // Close the menu
                    parentButton.focus(); // Return focus to the trigger button
                }
            }
            
            // Handle Enter and Space for button activation
            if ((e.key === 'Enter' || e.key === ' ') && activeElement.matches('[role="button"], [role="menuitem"]')) {
                e.preventDefault();
                activeElement.click();
            }
        });
    }

    private loadPlayerScript(callback?: () => void): void {        
        if (!window.isVjsPlayerScriptLoaded) {
            if (window.videojs && !window.vjsPlayer) {
                window.videojs = null;
            }
            const scriptElement: HTMLScriptElement = document.createElement('script');
            scriptElement.setAttribute('data-ot-ignore', '');
            scriptElement.onload = () => { 
                window.vjsPlayer = window.videojs;
                window.isVjsPlayerScriptLoaded = true;   
                this.loadEmePlugin(() => {
                    this.loadChromecastPlugin(() => {
                        callback?.();
                    });
                });
            };
            scriptElement.src = CONFIG.VIDEOJS_JS;
            this._containerElement.appendChild(scriptElement);
        }
        else {
            logger.notice('🎥 player script already loaded');
            callback?.();
        }
    }

    private loadEmePlugin(callback?: () => void): void {
        if (!window.isVjsEmePluginLoaded && CONFIG.VIDEOJS_EME_PLUGIN) {
            const scriptElement: HTMLScriptElement = document.createElement('script');
            scriptElement.setAttribute('data-ot-ignore', '');
            scriptElement.onload = () => { 
                window.isVjsEmePluginLoaded = true;   
                callback?.();
            };
            scriptElement.src = CONFIG.VIDEOJS_EME_PLUGIN;
            this._containerElement.appendChild(scriptElement);
        }
        else {
            callback?.();
        }
    }

    private loadChromecastPlugin(callback?: () => void): void {
        if (!window.isVjsChromecastPluginLoaded) {
            const scriptElement: HTMLScriptElement = document.createElement('script');
            scriptElement.setAttribute('data-ot-ignore', '');
            scriptElement.onload = () => { 
                window.isVjsChromecastPluginLoaded = true;   
                this.loadCastSenderScript(() => {
                    callback?.();
                });                
            };
            scriptElement.src = CONFIG.VIDEOJS_CHROMECAST_PLUGIN;
            this._containerElement.appendChild(scriptElement);
        }
        else {
            callback?.();
        }
    }

    private loadCastSenderScript(callback?: () => void): void {
        if (!window.isCastSenderScriptLoaded) {
            const scriptElement: HTMLScriptElement = document.createElement('script');
            scriptElement.setAttribute('data-ot-ignore', '');
            scriptElement.onload = () => { 
                window.isCastSenderScriptLoaded = true;   
                callback?.();
            };
            scriptElement.src = CONFIG.CHROMECAST_SENDER_JS;
            this._containerElement.appendChild(scriptElement);
        }
        else {
            callback?.();
        }
    }

    private loadYouboraScript(callback?: () => void): void {
        if (!window.isYouboraPluginScriptLoaded && !this._options?.isPreview) {            
            logger.notice('🎥 loading youbora script');
            const scriptElement: HTMLScriptElement = document.createElement('script');
            scriptElement.setAttribute('data-ot-ignore', '');
            scriptElement.onload = () => { 
                window.isYouboraPluginScriptLoaded = true;
                callback?.();
            };
            scriptElement.src = `${CONFIG.STORAGE_URL}/assets/js/youbora-6.7.7.min.js`;
            this._containerElement.appendChild(scriptElement);
        }
        else {
            logger.notice('🎥 youbora script already loaded');
            callback?.();
        }
    }

    private onIsWaitingChanged(isWaiting: boolean): void {
        this._isWaiting = isWaiting;
        if (this._isWaiting) {
            if (this._overlaysElement.classList.contains('m-vjs-player__overlays--show')) {
                this.addClass('u-hidden-important', '.m-vjs-player__loader');
                this.removeClass('u-hidden-important', '.m-vjs-player__overlay-loader');
            }
            else {
                this.removeClass('u-hidden-important', '.m-vjs-player__loader');
                this.addClass('u-hidden-important', '.m-vjs-player__overlay-loader');
            }
        }
        else {
            this.addClass('u-hidden-important', '.m-vjs-player__loader');
            this.addClass('u-hidden-important', '.m-vjs-player__overlay-loader');
        }
    }

    private onIsChromcastWaiting(isWaiting: boolean): void {
        this._node.dataset.isChromecastWaiting = isWaiting.toString();
        if (isWaiting) {
            this.hideOverlays();
            this.onIsWaitingChanged(true);
        }
        if (isWaiting && !this._isChromecastWaitingObserver) {
            this._isChromecastWaitingObserver = new MutationObserver(() => {
                if (document.querySelector('.vjs-tech-chromecast')) {
                    setTimeout(() => {
                        this.onIsChromcastWaiting(false); 
                        this.showOverlays();   
                    }, 1000);
                    this._isChromecastWaitingObserver.disconnect();
                    this._isChromecastWaitingObserver = null;                    
                }
            });
            this._isChromecastWaitingObserver.observe(this._node, {
                childList: true,
                subtree: true
            });
        }        
    }

    private showSkip(): void {  
        const skipText: string = (this._options || {}).skipText || USER_LOCALE.skipVideo;
        this._skipOverlayElement.querySelector('button').innerText = skipText;
        this._skipOverlayElement.classList.add('m-vjs-player__skip-overlay--show');        
    }

    private hideSkip(): void {  
        this._skipOverlayElement.classList.remove('m-vjs-player__skip-overlay--show');        
    }

    private showSkipIntro(): void {  
        this._skipIntroOverlayElement.classList.add('m-vjs-player__skip-intro-overlay--show');        
    }

    private hideSkipIntro(): void {  
        this._skipIntroOverlayElement.classList.remove('m-vjs-player__skip-intro-overlay--show');        
    }

    private showRating(): void {  
        if (!this.isOverlaysShown && this._metadata && this._metadata.rating && this._node.dataset.isChromecastWaiting !== 'true' && this._node.dataset.segmentsOpened !== 'true') {
            this._overlaysElement.classList.add('m-vjs-player__overlays--show-rating');
            this._showRatingPending = false;
            if (this._metadata.rating?.age >= 16) {
                this.play(this._ratingAlertElement);
            }
        }
    }
    
    private showSegments(): void {
        if (this._metadata && this._metadata.segments) {
            this._playerSegments.show();
            this._overlaysElement.classList.add('m-vjs-player__overlays--show-segments');
            this._node.dataset.segmentsOpened = 'true';            
            this._dataEventService.openFragment(this.mediaId, this._metadata.display, 'player');
        }
    }

    private closeSegments(): void {
        if (this._metadata && this._metadata.segments) {            
            this._overlaysElement.classList.add('m-vjs-player__overlays--hide-segments');
            setTimeout(() => {
                this._overlaysElement.classList.remove('m-vjs-player__overlays--hide-segments', 'm-vjs-player__overlays--show-segments');
                delete this._node.dataset.segmentsOpened;
            }, 500);            
        }
    }

    private closeRelated(): void {
        this._nextCountdown.stop();
        this._relatedClosed = true;
        this.hideOverlays();
    }

    private showRelated(): void {
        if (!this._overlaysElement.classList.contains('m-vjs-player__overlays--show-next') && !this._overlaysElement.classList.contains('m-vjs-player__overlays--show-related') && !this._relatedClosed) {
            const { embed } = (this._options || {});
            this.hideSkipIntro();
            const { next, related } = this._metadata;
            if (next && !next.isComingSoon) {
                if (!embed) {
                    this._overlaysElement.classList.add('m-vjs-player__overlays--show-next');
                    this._nextCountdown.start();
                }
            }
            else if (related) {
                if (!embed) {
                    related.viewAllClick = () => {  
                        this.closePlayer();
                    };
                    related.itemClick = () => {
                        this.closePlayer();
                    };
                }
                else {
                    related.viewAllClick = () => {
                        window.open(this._metadata.related.viewAllUrl, '_blank');
                    };
                }
                this._relatedRow.loadData(related, embed);
                this._overlaysElement.classList.add('m-vjs-player__overlays--show-related');
            }
            else {
                this._overlaysElement.classList.add('m-vjs-player__overlays--show');
                this.onIsWaitingChanged(this._isWaiting);
            }
        }
    }

    private showOverlays(): void {
        if (this._metadata && this._player) {
            if (this._node.dataset.isChromecastWaiting !== 'true' && this._node.dataset.segmentsOpened !== 'true') {
                this._overlaysElement.classList.remove('m-vjs-player__overlays--show-rating');
                if (this._node.dataset.status != 'ended' || this._relatedClosed) {
                    this._overlaysElement.classList.add('m-vjs-player__overlays--show');
                    this.onIsWaitingChanged(this._isWaiting);
                }
                else {
                    this.showRelated();
                }
            }
        }
    }

    private hideOverlays(force?: boolean): void {
        if (force || (this._isFirstPlay && this._node.dataset.status != 'ended' && (!this.isRelatedOverlayShown || this._relatedClosed))) {
            if (!this._audioMenu.isSubtitlesSettingsShown) {
                this.hideTooltips();
                this._overlaysElement.classList.remove('m-vjs-player__overlays--show', 'm-vjs-player__overlays--show-rating', 'm-vjs-player__overlays--show-next', 'm-vjs-player__overlays--show-related');
                this._overlaysElement.classList.remove('m-vjs-player__overlays--playback');
                this._qualityMenu.hide();
                this._playbackRateMenu?.hide();
                this._audioMenu.hide();
                this._signLanguagesMenu.hide();
                this.onIsWaitingChanged(this._isWaiting);
            }
        }
        if (this._showRatingPending) {
            setTimeout(() => {
                this.showRating();
                this.setHideOverlaysTimeout(CONFIG.PLAYER_RATING_TIME);
            }, 2000);
        }
    }

    private play(player: { play(): Promise<void> | undefined } | null | undefined): void {
        // play() rechaza con AbortError si se llama a pause() antes de que arranque
        // la reproducción, y con NotAllowedError si el navegador bloquea el play()
        // fuera de un gesto del usuario (p.ej. el aviso de edad en iOS). Ambos son
        // esperables y no deben acabar como unhandled rejection.
        player?.play()?.catch((e: any) => {
            if (e?.name !== 'AbortError' && e?.name !== 'NotAllowedError') {
                throw e;
            }
        });
    }

    private setHideOverlaysTimeout(timeout?: number): void {
        if (this._hideOverlaysTimeout) {
            clearTimeout(this._hideOverlaysTimeout);
            this._hideOverlaysTimeout = null;
        }
        // Always set timeout for both mouse and keyboard interactions
        this._hideOverlaysTimeout = setTimeout(() => {
            this.hideOverlays();
        }, timeout || CONFIG.PLAYER_INACTIVITY_TIMEOUT);
    }    

    private openMenu(menu: 'volume' | 'audio' | 'quality' | 'mini-player' | 'playbackRate' | 'sign-languages'): void {
        this.hideTooltips();
        this._volumeSelector.hide();
        
        if (menu != 'audio') {
            this._audioMenu.hide();
        }
        if (menu != 'sign-languages') {
            this._signLanguagesMenu.hide();
        }
        if (menu != 'quality') {
            this._qualityMenu.hide();
        }
        if (menu != 'playbackRate') {
            this._playbackRateMenu?.hide();
        }
        if (menu != 'mini-player') {
            this._miniPlayerMenu.hide();
        }
        if (menu == 'volume') {
            this._volumeSelector.show();            
        }
        if (menu == 'audio') {
            this._audioMenu.toggle();
        }
        if (menu == 'sign-languages') {
            this._signLanguagesMenu.toggle();
        }
        if (menu == 'quality') {     
            this._qualityMenu.toggle();       
        }
        if (menu == 'playbackRate') {     
            this._playbackRateMenu.toggle();       
        }
        if (menu == 'mini-player') {
            this._miniPlayerMenu.toggle();
        }
        this.setHideOverlaysTimeout();
    }

    private setNext(): void {
        const { next } = this._metadata;
        if (next) {
            this.setInnerHTML(next.title, '.m-vjs-player__next-title');
        }
    }

    private setOptions() {
        const { miniPlayer, back, minimize, chromecast, skip, embed } = (this._options || {})

        const canOpenMiniPlayer: boolean = miniPlayer !== 'disable';
        if (!embed && canOpenMiniPlayer && !Agent.iPhone() && document.pictureInPictureEnabled) {
            this.removeClass('u-hidden', '.m-vjs-player__mini-player-control');
        }
        else {
            this.addClass('u-hidden', '.m-vjs-player__mini-player-control');
        }
        const canBack: boolean = back;
        if (embed || canBack === false) {
            this.addClass('u-hidden-important', '.m-vjs-player__back');
        }
        else {
            this.removeClass('u-hidden-important', '.m-vjs-player__back');            
        }
        const canMinimize: boolean = minimize;
        if (canMinimize === false) {
            this.addClass('u-hidden-important', '.m-vjs-player__full-screen');
        }
        else {
            this.removeClass('u-hidden-important', '.m-vjs-player__full-screen');            
        }
        const canChromecast: boolean = chromecast;
        if (embed || canChromecast === false) {
            this.addClass('u-hidden-important', '.m-vjs-player__chromecast-control');
        }
        else {
            this.removeClass('u-hidden-important', '.m-vjs-player__chromecast-control');            
        }
        const canSkip: boolean = skip;
        if (!embed && canSkip === true) {
            this.showSkip();
        }
        else {
            this.hideSkip();
        }
    }
    
    public get isMiniPlayerActive(): boolean {
        return this._node.dataset.miniplayer === 'true';
    }

    private setMiniPlayerActive(value: boolean, pip?: boolean): void {
        if (value) {
            this._node.dataset.miniplayer = 'true'; 
        } 
        else {        
            delete this._node.dataset.miniplayer;
            this.exitPictureInPicture();
        }
    }

    private requestPictureInPicture(): void {
        if (document.pictureInPictureEnabled) {
            this._node.querySelector('video')?.requestPictureInPicture().catch(e => logger.warn('🎥 picture in picture request rejected', e));
        }
    }

    private exitPictureInPicture(): void {
        if (document.pictureInPictureEnabled && document.pictureInPictureElement) {
            document.exitPictureInPicture();
        }        
    }

    private toggleMiniPlayer(options?: { closing?: boolean, pip?: boolean }): void {
        const { closing, pip } = options || {};
        const OVERLAYS_DISABLED_CLASS: string = 'm-vjs-player__overlays--disabled';
        if (this.isMiniPlayerActive) {
            this._overlaysElement.classList.add(OVERLAYS_DISABLED_CLASS);
            this.setMiniPlayerActive(false);
            delete this._node.dataset.notransitions;
            Dom.removeDraggable(this._node);
            this.hideBodyOverflow();
            if (closing) {
                this._overlaysElement.classList.remove(OVERLAYS_DISABLED_CLASS);
                this.dispatchCustomEvent('mini-player-deactivated', { closing });
            }
            else {
                setTimeout(() => {
                    this._overlaysElement.classList.remove(OVERLAYS_DISABLED_CLASS);
                    this.dispatchCustomEvent('mini-player-deactivated', { closing });
                }, TRANSITION_TIME);
            }
        }
        else {
            const isFullScreen: boolean = Dom.isFullScreen();
            isFullScreen && this.setFullScreenMode();
            Dom.offFullScreen();
            Dom.unlockOrientation();
            this._overlaysElement.classList.add(OVERLAYS_DISABLED_CLASS);
            this.setMiniPlayerActive(true);
            this.showBodyOverflow();
            // Safari solo permite PiP durante ~1s tras la interacción del usuario: no se puede esperar a la transición
            if (pip) {
                this.requestPictureInPicture();
            }
            setTimeout(() => {
                this._node.dataset.notransitions = 'true';
                Dom.makeDraggable(this._node);
                this._overlaysElement.classList.remove(OVERLAYS_DISABLED_CLASS);
                this.dispatchCustomEvent('mini-player-activated');
            }, TRANSITION_TIME);
        }
        this.hideTooltips();
    }

    public closeMiniPlayer(): void {
        if (this.isMiniPlayerActive) {
            this.toggleMiniPlayer({ closing: true });
            this.closePlayer();
        }
    }

    private closePlayer(e?: { ended?: boolean, skipped?: boolean }): void {
        this.dispatchCustomEvent('player-close', e);
    }

    public hide(): void { 
        this.isChromecastActive = false;
        this.stopChromecast();
        this._showRatingPending = null; 
        this.hideTooltips();  
        this.dispatchCustomEvent('hidding');
        this._nextCountdown.stop();
        this._volumeSelector.hide();
        this._audioMenu.hide();
        this._signLanguagesMenu.hide();
        this._qualityMenu.hide();
        this._playbackRateMenu?.hide();
        this.hideOverlays(true);
        this.hideSkipIntro();
        this.hideSkip();
        if (this._hideOverlaysTimeout) {
            clearInterval(this._hideOverlaysTimeout);
            this._hideOverlaysTimeout = null;
        }      
        if (this._player)   {
            this._player.pause();
        }
        Dom.offFullScreen();
        Dom.unlockOrientation();
    }

    public unload(hide: boolean = true): void {
        this.isChromecastActive = false;
        if (hide) {
            this.hide();
            Dom.offFullScreen();            
        }
        else {
            this.hideOverlays(true);
            this.onIsWaitingChanged(true);
        }
        if (this.isMiniPlayerActive) {
            this.closeMiniPlayer();
        }
        this._metadata = null;
        this._isFirstPlay = false;
        this._qualitiesLoaded = false;
        this._playbackRatesLoaded = false;
        this._qualityMenu.unload();
        this._playbackRateMenu?.unload();
        this._audioLoaded = false;
        this._audioMenu.unload();
        this._signLanguagesMenu.unload();
        this._signLanguagesLoaded = false;
        this.addClass('u-hidden', '.m-vjs-player__sign-languages-control');
        if (this._player) {
            this._player.dispose();
            this._player = null;
        }
        this.disposeVjsPlayers();
        this._isLoaded = false;
    }

    // Si un player anterior no se ha destruido (error en unload, unload no llamado...), su <video-js id="vjs-player">
    // sigue registrado y videojs('vjs-player') devuelve esa instancia antigua ignorando las nuevas opciones
    private disposeVjsPlayers(): void {
        this._containerElement.querySelectorAll('video-js, .video-js').forEach((element: any) => {
            try {
                element.player?.dispose();
            }
            catch (e: any) {
                logger.error('🎥 error disposing previous player', e);
            }
            element.remove();
        });
    }

    public dispose(): void {
        this.unload();
        super.dispose();
    }

    private get isOverlaysShown(): boolean {
        return this._overlaysElement.classList.contains('m-vjs-player__overlays--show')
            || this._overlaysElement.classList.contains('m-vjs-player__overlays--show-next')
            || this._overlaysElement.classList.contains('m-vjs-player__overlays--show-related')
            || this._overlaysElement.classList.contains('m-vjs-player__overlays--show-rating');
    }

    private get isRelatedOverlayShown(): boolean {
        return this._overlaysElement.classList.contains('m-vjs-player__overlays--show-related') || this._overlaysElement.classList.contains('m-vjs-player__overlays--show-next');
    }

    public get isLoaded(): boolean {
        return this._isLoaded;
    }   

    public get mediaId(): string {
        return this._metadata?.media?.id;
    }

    public get rating(): any {
        return this._metadata?.rating;
    }

    public get ratingTags(): any[] {
        return this._metadata?.ratingTags;
    }
    
    public get currentTime(): number {
        return this._player?.currentTime();
    }
    
    public get currentWatchingProgressTime(): number {
        return this.isRelatedOverlayShown || this.isCreditsStarted(this._player?.currentTime()) ? this.duration: this._player?.currentTime();
    }

    private isCreditsStarted(currentTime: number): boolean {
        let isStarted: boolean = false;
        const { timeMarkers } = this._metadata || {};
        if (timeMarkers?.length) {
            const marker: { type: 'intro' | 'credits', start: number, end: number } = timeMarkers.find(m => m.start <= currentTime && m.end >= currentTime);
            if (marker) {
                if (marker.type === 'credits') {                    
                    isStarted = true;
                }
            }
        }
        return isStarted;
    }

    private processError(): void {
        const error: any = this._player.error();
        this._player.pause();
        if (error?.message) {
            const errorModal: HTMLDivElement = this.node.querySelector('.vjs-modal-dialog-content');
            let title = USER_LOCALE.technicalError;
            let description = errorModal?.textContent || error.message;
            const sessionId = this._sessionService.getSessionId();
            if (error.code === 101) {
                title = undefined;
            }
            errorModal.innerText = '';
            const iconElement: HTMLDivElement = document.createElement('div'); 
            iconElement.classList.add('m-vjs-player__error-icon')
            iconElement.innerHTML = this._ejsService.render('atoms/icon/icon.ejs', { key: 'warning' });
            errorModal.appendChild(iconElement);
            const titleElement: HTMLDivElement = document.createElement('div');
            titleElement.classList.add('m-vjs-player__error-title')
            titleElement.innerText = title;            
            errorModal.appendChild(titleElement);
            const descriptionElement: HTMLDivElement = document.createElement('div');
            descriptionElement.classList.add('m-vjs-player__error-description')
            descriptionElement.innerText = description;            
            errorModal.appendChild(descriptionElement);
            const buttonElement: HTMLButtonElement = document.createElement('button');
            buttonElement.classList.add('m-vjs-player__error-close', 'u-button', 'u-button--primary');
            buttonElement.innerHTML = USER_LOCALE.ok;
            buttonElement.onclick = () => this.closePlayer();            
            errorModal.appendChild(buttonElement);
            const sessionElement: HTMLDivElement = document.createElement('div');
            sessionElement.classList.add('m-vjs-player__error-session');
            const sessionSpan: HTMLSpanElement = document.createElement('span');
            sessionSpan.innerText = `${sessionId?.toUpperCase()} - ${new Date().getTime()}`;
            sessionElement.appendChild(sessionSpan);            
            errorModal.appendChild(sessionElement);
        }
    }
    
    public get duration(): number {
        return this._player?.duration();
    }

    public get isFirstPlay(): boolean {
        return this._isFirstPlay;
    }

    public get selectedTextTrackLanguage(): string {
        return this._audioMenu?.selectedTextTrackLanguage;
    }   

    public get selectedAudioTrackLanguage(): string {
        return this._audioMenu?.selectedAudioTrackLanguage;
    } 

    public get isChromecastActive(): boolean {
        return this.node.dataset.isChromecastActive === 'true';
    }

    public set isChromecastActive(value: boolean) {
        this.node.dataset.isChromecastActive = value.toString();
    }

    public get sendDataEvent(): boolean {
        return !this.isChromecastActive;
    }

    public setDisposable(value: boolean): void {
        this.isDisposable = value;
        this._timeLine.isDisposable = value;
        this._volumeSelector.isDisposable = value;
        this._audioMenu.setDisposable(value);
        this._signLanguagesMenu.isDisposable = value;
        this._qualityMenu.isDisposable = value;
        this._playbackRateMenu.isDisposable = value;
        this._miniPlayerMenu.isDisposable = value;
        this._nextCountdown.isDisposable = value;
        this._playerSegments.isDisposable = value;
    }
}